import React, { useEffect, useMemo, useState } from "react";
import { Hooks, Request } from "@xt/xtbase";
import ProptradingLayout from "@/components/Pages/proptrading/ProptradingLayout";
import propDefaultAvatar from "@/assets/img/proptrading/account-partner-friend-avatar.png";
import { get_prop_home, get_prop_leaderboard, PropLeaderboardItemVO, PropLeaderboardVO } from "@/api/v4/proptrading";
import { propQuoteDisplayUnit, isPropLiveStage } from "@/utils/proptrading/propHomeUtils";
import styles from "./index.module.scss";

/** 排行榜接口就绪后改为 false，恢复真实请求 */
const RANKING_USE_MOCK = false;

const { useTranslation } = Hooks;

type PeriodTab = "day" | "week" | "month";
type ArenaTab = "newbie" | "elite" | "master" | "divine";

/** 与后端 StageEnum 对齐 */
const ARENA_TO_STAGE: Record<ArenaTab, string> = {
  newbie: "ROOKIE",
  elite: "ELITE",
  master: "MASTER",
  divine: "LIVE",
};

const PERIOD_TO_API: Record<PeriodTab, string> = {
  day: "DAY",
  week: "WEEK",
  month: "MONTH",
};

const RANK_COLORS: Record<number, string> = {
  1: "#ffa200",
  2: "#bbdcf4",
  3: "#ff7300",
};

function staticImgSrc(m: string | { src?: string }) {
  return typeof m === "string" ? m : m?.src ?? String(m);
}

const RANKING_LIST_AVATAR = staticImgSrc(propDefaultAvatar);

/** 与 GET /prop/leaderboard 返回体一致的结构，便于联调前预览 UI */
function mockPropLeaderboard(stage: string, period: string): PropLeaderboardVO {
  const n = 12;
  let h = 0;
  for (let i = 0; i < stage.length; i++) h = (h + stage.charCodeAt(i) * (i + 1)) % 997;
  for (let i = 0; i < period.length; i++) h = (h + period.charCodeAt(i) * (i + 3)) % 997;

  const list: PropLeaderboardItemVO[] = Array.from({ length: n }, (_, i) => {
    const rank = i + 1;
    const jitter = ((h + rank * 31) % 200) - 80;
    const profit = 5000 + (n - rank) * 420 + jitter * 12;
    const profitPercent = 2.5 + (n - rank) * 0.85 + (jitter % 15) * 0.1;
    const rewardAmount = Math.max(0, 800 - rank * 55 + (jitter % 40));
    return {
      rank,
      userId: 880_000_000 + h * 1000 + rank * 17,
      profit,
      profitPercent: Number(profitPercent.toFixed(2)),
      rewardAmount: Number(rewardAmount.toFixed(2)),
    };
  });

  return {
    list,
    selfRank: null,
    totalCount: n,
  };
}

function formatAmount(n: number) {
  if (n == null || Number.isNaN(Number(n))) return "0";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatProfit(n: number) {
  const v = Number(n);
  if (Number.isNaN(v)) return "0";
  if (v > 0) return `+${formatAmount(v)}`;
  return formatAmount(v);
}

function formatPercent(n: number) {
  const v = Number(n);
  if (Number.isNaN(v)) return "0%";
  return `${v.toFixed(2)}%`;
}

const ProptradingRankingPage: React.FC = () => {
  const t = useTranslation();
  const [period, setPeriod] = useState<PeriodTab>("day");
  const [arena, setArena] = useState<ArenaTab>("newbie");
  const [list, setList] = useState<PropLeaderboardItemVO[]>([]);
  const [userStageFromHome, setUserStageFromHome] = useState<string | null>(null);

  const prizeUnit = useMemo(
    () => propQuoteDisplayUnit(isPropLiveStage(userStageFromHome)),
    [userStageFromHome],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { err, data } = await Request.XtAxios4.asyncAwait(get_prop_home);
      if (cancelled) return;
      if (!err && data != null && typeof data === "object") {
        const st = (data as { userStage?: string | null }).userStage;
        setUserStageFromHome(st != null ? String(st) : null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** 神域挑战（LIVE）仅支持月榜 */
  useEffect(() => {
    if (arena === "divine") {
      setPeriod("month");
    } else {
      setPeriod((p) => (p === "month" ? "day" : p));
    }
  }, [arena]);

  useEffect(() => {
    let cancelled = false;
    const stage = ARENA_TO_STAGE[arena];
    const periodForRequest: PeriodTab = arena === "divine" ? "month" : period;
    const periodApi = PERIOD_TO_API[periodForRequest];

    if (RANKING_USE_MOCK) {
      const body = mockPropLeaderboard(stage, periodApi);
      setList(body.list);
      return;
    }

    void (async () => {
      const { err, data } = await Request.XtAxios4.asyncAwait(get_prop_leaderboard, {
        params: { stage, period: periodApi },
      });
      if (cancelled) return;
      if (err) {
        setList([]);
        return;
      }
      const body = data as PropLeaderboardVO;
      setList(Array.isArray(body?.list) ? body.list : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [period, arena]);

  return (
    <ProptradingLayout title={t("proptrading.ranking.title")} activeTab="ranking">
      <div className={styles.page}>
        <div className={styles.titleRow}>
          <div className={styles.periodTabs}>
            {arena === "divine" ? (
              <button type="button" className={styles.periodTab} data-active={true}>
                <span>{t("proptrading.ranking.periodMonth")}</span>
                <div className={styles.periodUnderline} />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.periodTab}
                  data-active={period === "day"}
                  onClick={() => setPeriod("day")}
                >
                  <span>{t("proptrading.ranking.periodDay")}</span>
                  <div className={styles.periodUnderline} />
                </button>
                <button
                  type="button"
                  className={styles.periodTab}
                  data-active={period === "week"}
                  onClick={() => setPeriod("week")}
                >
                  <span>{t("proptrading.ranking.periodWeek")}</span>
                  <div className={styles.periodUnderline} />
                </button>
              </>
            )}
          </div>
          <div className={styles.updatedAt}>{t("proptrading.ranking.updatedAt")}</div>
        </div>

        <div className={styles.arenaNav}>
          <button
            type="button"
            className={styles.arenaTab}
            data-active={arena === "newbie"}
            onClick={() => setArena("newbie")}
          >
            {t("proptrading.ranking.arenaNewbie")}
          </button>
          <button
            type="button"
            className={styles.arenaTab}
            data-active={arena === "elite"}
            onClick={() => setArena("elite")}
          >
            {t("proptrading.ranking.arenaElite")}
          </button>
          <button
            type="button"
            className={styles.arenaTab}
            data-active={arena === "master"}
            onClick={() => setArena("master")}
          >
            {t("proptrading.ranking.arenaMaster")}
          </button>
          <button
            type="button"
            className={styles.arenaTab}
            data-active={arena === "divine"}
            onClick={() => setArena("divine")}
          >
            {t("proptrading.ranking.arenaDivine")}
          </button>
        </div>

        <div className={styles.list}>
          {list.length === 0 ? (
            <div className={styles.empty}>{t("proptrading.ranking.empty")}</div>
          ) : (
            list.map((item, index) => {
              const rank = item.rank;
              return (
                <div key={`${rank}-${item.userId}-${index}`} className={styles.rankCard}>
                  <div className={styles.rankBadge} style={{ color: RANK_COLORS[rank] ?? "rgba(255,255,255,0.56)" }}>
                    {rank}
                  </div>
                  <div className={styles.rankContent}>
                    <img src={RANKING_LIST_AVATAR} alt="" className={styles.avatar} />
                    <div className={styles.rankInfo}>
                      <div className={styles.rankEmail}>{String(item.userId ?? "")}</div>
                      <div className={styles.rankMeta}>
                        <span>{formatProfit(item.profit)}</span>
                        <span>{formatPercent(item.profitPercent)}</span>
                      </div>
                    </div>
                    <div className={styles.rankPrize}>
                      <div className={styles.prizeLabel}>{t("proptrading.ranking.prize")}</div>
                      <div className={styles.prizeValue}>
                        <span>{formatAmount(item.rewardAmount)}</span>
                        <span>{prizeUnit}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </ProptradingLayout>
  );
};

export default ProptradingRankingPage;
